import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Stack,
    Typography,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Snackbar,
    Alert,
} from "@mui/material";
import { useState } from "react";
import dayjs from "dayjs";
import TrainingDetailDialog from "./TrainingDetailDialog.tsx";
import { trainingService } from "../api/service.ts";
import { TrainingSession } from "../api/types";
import { useAuth } from "../AuthContext.tsx";

type TrainingHistoryDialogProps = {
    open: boolean;
    onClose: () => void;
};

export default function TrainingHistoryDialog({ open, onClose }: TrainingHistoryDialogProps) {
    const { isAdmin } = useAuth();
    const [startDate, setStartDate] = useState(dayjs().subtract(1, "year").format("YYYY-MM-DD"));
    const [endDate, setEndDate] = useState(dayjs().format("YYYY-MM-DD"));
    const [trainings, setTrainings] = useState<TrainingSession[]>([]);
    const [selected, setSelected] = useState<TrainingSession | null>(null);

    const [confirmDelete, setConfirmDelete] = useState<TrainingSession | null>(null);
    const [deleting, setDeleting] = useState(false);

    // Snackbar state
    const [snackbar, setSnackbar] = useState<{
        open: boolean;
        message: string;
        severity: "success" | "error";
    }>({ open: false, message: "", severity: "success" });

    const handleSearch = async () => {
        const data = await trainingService.getTrainingsBetween(startDate, endDate);
        setTrainings(data);
    };

    const handleDelete = async () => {
        if (!confirmDelete) return;
        try {
            setDeleting(true);
            await trainingService.deleteTraining(confirmDelete.id);
            setTrainings((prev) => prev.filter((t) => t.id !== confirmDelete.id));
            setConfirmDelete(null);
            setSnackbar({
                open: true,
                message: "Allenamento eliminato con successo.",
                severity: "success",
            });
        } catch (err) {
            console.error("Errore durante l'eliminazione:", err);
            setSnackbar({
                open: true,
                message: "Errore durante l'eliminazione dell'allenamento.",
                severity: "error",
            });
        } finally {
            setDeleting(false);
        }
    };

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
                <DialogTitle sx={{ backgroundColor: "primary.main" }}>Storico allenamenti</DialogTitle>
                <DialogContent>
                    <Stack direction="row" spacing={1} mb={1} mt={2}>
                        <TextField
                            label="Dal"
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            fullWidth
                        />
                        <TextField
                            label="Al"
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            fullWidth
                        />
                        <Button variant="contained" onClick={handleSearch}>
                            Cerca
                        </Button>
                    </Stack>

                    {trainings.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                            Nessun allenamento trovato
                        </Typography>
                    ) : (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Data</TableCell>
                                    <TableCell>Numero set</TableCell>
                                    <TableCell>Risultato</TableCell>
                                    <TableCell>Azioni</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {trainings.map((t) => (
                                    <TableRow key={t.id}>
                                        <TableCell>{t.date}</TableCell>
                                        <TableCell>{t.sets.length}</TableCell>
                                        <TableCell>
                                            {t.sets.filter((s) => s.scoreA > s.scoreB).length} -{" "}
                                            {t.sets.filter((s) => s.scoreB > s.scoreA).length}
                                        </TableCell>
                                        <TableCell>
                                            <Stack direction="row" spacing={1}>
                                                <Button
                                                    size="small"
                                                    onClick={() => setSelected(t)}
                                                >
                                                    Dettagli
                                                </Button>
                                                {isAdmin && (
                                                    <Button
                                                        size="small"
                                                        color="error"
                                                        onClick={() => setConfirmDelete(t)}
                                                    >
                                                        Elimina
                                                    </Button>
                                                )}
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>Chiudi</Button>
                </DialogActions>
            </Dialog>

            {selected && (
                <TrainingDetailDialog
                    training={selected}
                    onClose={() => setSelected(null)}
                />
            )}

            {/* Dialog di conferma eliminazione */}
            <Dialog
                open={!!confirmDelete}
                onClose={() => !deleting && setConfirmDelete(null)}
            >
                <DialogTitle>Conferma eliminazione</DialogTitle>
                <DialogContent>
                    <Typography>
                        Sei sicuro di voler eliminare l'allenamento del{" "}
                        <strong>{confirmDelete?.date}</strong>?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setConfirmDelete(null)}
                        disabled={deleting}
                    >
                        Annulla
                    </Button>
                    <Button
                        color="error"
                        variant="contained"
                        onClick={handleDelete}
                        disabled={deleting}
                    >
                        {deleting ? "Eliminando..." : "Elimina"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar feedback */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ width: "100%" }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
}
